import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { ApiError } from '@app/api/ApiError';
import { get, post, put, withPath, type ApiPath } from '@app/api/typed';
import {
  forQuery,
  type CreateUserResult,
  type User,
  type UserCreate,
  type UsersFilterValues,
  type UsersPage,
  type UserUpdate,
} from './UserModel';

/**
 * Hooks de T22 (FR-106, FR-107).
 *
 * Toda mutação invalida a lista: quem manda no que a tela mostra é o que ficou
 * gravado, não o que foi enviado.
 */
export const usersKeys = {
  todas: ['users'] as const,
  lista: (filters: UsersFilterValues) => ['users', 'list', forQuery(filters)] as const,
};

/** URL com `{id}` preenchido sem perder o tipo do path dos helpers tipados. */
function rota<P extends ApiPath>(template: P, id: string): P {
  return withPath(template, { id }) as P;
}

export function useUsers(filters: UsersFilterValues): UseQueryResult<UsersPage, ApiError> {
  return useQuery<UsersPage, ApiError>({
    queryKey: usersKeys.lista(filters),
    queryFn: async () => {
      const page = await get('/api/v1/users', { params: forQuery(filters) });
      return { ...page, items: (page.items ?? []) as User[] };
    },
    // Trocar de página/filtro sem piscar a grade inteira de volta ao esqueleto.
    placeholderData: keepPreviousData,
  });
}

/** Criar (FR-106). `senha_inicial` volta só aqui, e só quando foi gerada. */
export function useCreateUser(): UseMutationResult<CreateUserResult, ApiError, UserCreate> {
  const queryClient = useQueryClient();
  return useMutation<CreateUserResult, ApiError, UserCreate>({
    mutationFn: (body) => post('/api/v1/users', { body }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: usersKeys.todas }),
  });
}

export interface UpdateUserVars {
  id: string;
  body: UserUpdate;
}

/** Editar nome e papel — o e-mail é imutável (12.2); rebaixar o último admin dá 409. */
export function useUpdateUser(): UseMutationResult<User, ApiError, UpdateUserVars> {
  const queryClient = useQueryClient();
  return useMutation<User, ApiError, UpdateUserVars>({
    mutationFn: ({ id, body }) => put(rota('/api/v1/users/{id}', id), { body }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: usersKeys.todas }),
  });
}

/** Desativar (encerra as sessões do usuário imediatamente — AC-06). */
export function useDeactivateUser(): UseMutationResult<User, ApiError, string> {
  const queryClient = useQueryClient();
  return useMutation<User, ApiError, string>({
    mutationFn: (id) => post(rota('/api/v1/users/{id}/deactivate', id)),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: usersKeys.todas }),
  });
}

export function useReactivateUser(): UseMutationResult<User, ApiError, string> {
  const queryClient = useQueryClient();
  return useMutation<User, ApiError, string>({
    mutationFn: (id) => post(rota('/api/v1/users/{id}/reactivate', id)),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: usersKeys.todas }),
  });
}

export type PasswordReset = { password: string };

/**
 * Redefinir senha (FR-107, R20): sem corpo, o servidor gera. A senha volta uma
 * única vez — não há autoatendimento nem segunda chance de ler (FR-109).
 */
export function useResetPassword(): UseMutationResult<PasswordReset, ApiError, string> {
  const queryClient = useQueryClient();
  return useMutation<PasswordReset, ApiError, string>({
    mutationFn: (id) => post(rota('/api/v1/users/{id}/reset-password', id), { body: {} }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: usersKeys.todas }),
  });
}

/**
 * Um usuário pelo id — só para o drawer aberto por URL (`?drawer=<uuid>`) cujo
 * registro não está na página carregada. Enquanto ele estiver na grade, a lista
 * já é a fonte.
 */
export function useUser(id: string | null): UseQueryResult<User, ApiError> {
  return useQuery<User, ApiError>({
    queryKey: ['users', 'detail', id] as const,
    queryFn: () => get(rota('/api/v1/users/{id}', id as string)),
    enabled: Boolean(id),
  });
}
